import DictionaryApiService from "../admin/system/integrate/DictionaryApiService";

const DatamuseService = {
    // Helper function to convert plural words to singular
    toSingular: (word) => {
        if (!word || word.length <= 3) return word; // Don't modify short words
        
        const lowerWord = word.toLowerCase();
        
        // Common plural to singular conversions
        if (lowerWord.endsWith('ies')) {
            return word.slice(0, -3) + 'y'; // comedies -> comedy
        }
        if (lowerWord.endsWith('s')) {
            // Don't remove 's' if the word ends with 'ss' (like 'boss', 'glass')
            if (lowerWord.endsWith('ss')) {
                return word;
            }
            // Don't remove 's' if the word ends with 'us' (like 'focus', 'campus')
            if (lowerWord.endsWith('us')) {
                return word;
            }
            // Don't remove 's' if the word ends with 'is' (like 'basis', 'crisis')
            if (lowerWord.endsWith('is')) {
                return word;
            }
            // Remove 's' for most other cases
            return word.slice(0, -1);
        }
        
        return word;
    },

    wildcardSearch: async (word, language = 'english') => {
        // Skip Datamuse API for French (Datamuse only supports English)
        // if (language === 'french') {
        //     return [];
        // }
        
        try {
            const response = await fetch(`https://api.datamuse.com/words?sp=${word}&md=f`);

            if (!response.ok) {
                const error = new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
                error.status = response.status;
                error.statusText = response.statusText;
                throw error;
            }

            const data = await response.json();

            // If no results, return empty array
            if (!data || data.length === 0) {
                return [];
            }

            // Extract all words from datamuse results
            const words = data.map(item => item.word);

            // Call getMultipleWordsInfo to check which words have meanings
            const wordsInfo = await DictionaryApiService.getMultipleWordsInfo(words, language, true);

            // Create a Set of words that have meanings for quick lookup
            const wordsWithMeanings = new Set(
                wordsInfo
                    .filter(item => item.meaning && Object.keys(item.meaning).length > 0)
                    .map(item => item.word.toLowerCase())
            );

            // Filter datamuse results to only include words that have meanings
            const filteredResults = data.filter(item => 
                wordsWithMeanings.has(item.word.toLowerCase())
            );

            console.log("Filtered wildcardSearch results (with meanings):", filteredResults);
            return filteredResults; // Returns an array of words related to the input word that have meanings
        } catch (error) {
            console.error("Error querying the word:", error);
            throw error; // Propagate the error so it can be handled by the calling code
        }
    },

    associationSearch: async (word, language = 'english') => {
        // Skip Datamuse API for French (Datamuse only supports English)
        // if (language === 'french') {
        //     return [];
        // }
        
        try {
            // Convert plural to singular for better search results
            const singularWord = DatamuseService.toSingular(word);
            
            const response = await fetch(`https://api.datamuse.com/words?rel_trg=${singularWord}`);

            if (!response.ok) {
                const error = new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
                error.status = response.status;
                error.statusText = response.statusText;
                throw error;
            }

            const data = await response.json();

            console.log("associationSearch");
            console.log(data);

            // If no results, return empty array
            if (!data || data.length === 0) {
                return [];
            }

            // Extract all words from datamuse results
            const words = data.map(item => item.word);

            // Call getMultipleWordsInfo to check which words have meanings
            const wordsInfo = await DictionaryApiService.getMultipleWordsInfo(words, language, true);

            // Create a Set of words that have meanings for quick lookup
            const wordsWithMeanings = new Set(
                wordsInfo
                    .filter(item => item.meaning && Object.keys(item.meaning).length > 0)
                    .map(item => item.word.toLowerCase())
            );

            // Filter datamuse results to only include words that have meanings
            const filteredResults = data.filter(item => 
                wordsWithMeanings.has(item.word.toLowerCase())
            );

            console.log("Filtered associationSearch results (with meanings):", filteredResults);
            return filteredResults; // Returns an array of words related to the input word that have meanings
        } catch (error) {
            console.error("Error querying the word:", error);
            throw error; // Propagate the error so it can be handled by the calling code
        }
    },

    synonymSearch: async (word, language = 'english') => {
        // Skip Datamuse API for French (Datamuse only supports English)
        if (language === 'french') {
            return [];
        }
        
        try {
            // Convert plural to singular for better search results
            const singularWord = DatamuseService.toSingular(word);
            
            const response = await fetch(`https://api.datamuse.com/words?rel_syn=${singularWord}`);

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const data = await response.json();

            // If no results, return empty array
            if (!data || data.length === 0) {
                return [];
            }

            // Extract all words from datamuse results
            const words = data.map(item => item.word);

            // Call getMultipleWordsInfo to check which words have meanings
            const wordsInfo = await DictionaryApiService.getMultipleWordsInfo(words, language, true);

            // Create a Set of words that have meanings for quick lookup
            const wordsWithMeanings = new Set(
                wordsInfo
                    .filter(item => item.meaning && Object.keys(item.meaning).length > 0)
                    .map(item => item.word.toLowerCase())
            );

            // Filter datamuse results to only include words that have meanings
            const filteredResults = data.filter(item => 
                wordsWithMeanings.has(item.word.toLowerCase())
            );

            console.log("Filtered synonymSearch results (with meanings):", filteredResults);
            return filteredResults; // Returns an array of synonyms that have meanings
        } catch (error) {
            console.error("Error querying synonyms:", error);
            throw error; // Propagate the error so it can be handled by the calling code
        }
    },

    requestWordScores: async (wordList) => {
        try {

            // Fetch word scores for all words concurrently
            const wordScorePromises = wordList.map(async (word) => {
                try {
                    const response = await fetch(`https://api.datamuse.com/words?sp=${word}&md=f`);
                    
                    if (!response.ok) {
                        throw new Error(`Network response was not ok for word: ${word}`);
                    }
                    
                    const data = await response.json();

                    // Find exact match (case-insensitive)
                    const exactMatch = data.find(item => item.word.toLowerCase() === word.toLowerCase());
                    
                    if (exactMatch) {
                        return { word, item: exactMatch };
                    }
                    
                    // Fallback to first result if no exact match
                    const fallbackItem = data.length > 0 ? data[0] : null;
                    return { word, item: fallbackItem };
                    
                } catch (error) {
                    console.error(`Error fetching score for word "${word}":`, error);
                    return { word, item: null, error: error.message };
                }
            });

            const wordResults = await Promise.all(wordScorePromises);
            
            // Calculate total score and valid scores count
            let totalScore = 0;
            let validScores = 0;
            
            wordResults.forEach(result => {
                if (result.item && result.item.score) {
                    totalScore += result.item.score;
                    validScores++;
                }
            });
            
            
            return {
                scores: wordResults,
                totalScore: totalScore,
                validScores: validScores,
                wordCount: wordList.length
            };
        } catch (error) {
            console.error("Error querying word scores:", error);
            throw error;
        }
    },

    // Get word frequency using Datamuse API
    getWordFrequency: async (word) => {
        try {
            const response = await fetch(`https://api.datamuse.com/words?sp=${word}&md=f`);

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const data = await response.json();
            
            // Find exact match (case-insensitive)
            const exactMatch = data.find(item => item.word.toLowerCase() === word.toLowerCase());
            
            if (exactMatch && exactMatch.tags) {
                // Extract frequency from tags array
                const frequencyTag = exactMatch.tags.find(tag => tag.startsWith('f:'));
                if (frequencyTag) {
                    const frequency = parseFloat(frequencyTag.substring(2)); // Remove 'f:' prefix
                    return {
                        word: exactMatch.word,
                        frequency: frequency,
                        score: exactMatch.score || 0
                    };
                }
            }
            
            return {
                word: word,
                frequency: null,
                score: 0,
                error: "Frequency data not found"
            };
            
        } catch (error) {
            console.error("Error querying word frequency:", error);
            throw error;
        }
    },

    // Get frequencies for multiple words
    getWordFrequencies: async (wordList) => {
        try {
            const frequencyPromises = wordList.map(async (word) => {
                try {
                    return await DatamuseService.getWordFrequency(word);
                } catch (error) {
                    console.error(`Error fetching frequency for word "${word}":`, error);
                    return {
                        word: word,
                        frequency: null,
                        score: 0,
                        error: error.message
                    };
                }
            });

            const results = await Promise.all(frequencyPromises);
            
            // Calculate statistics
            const validFrequencies = results.filter(result => result.frequency !== null);
            const totalFrequency = validFrequencies.reduce((sum, result) => sum + result.frequency, 0);
            const averageFrequency = validFrequencies.length > 0 ? totalFrequency / validFrequencies.length : 0;
            
            return {
                frequencies: results,
                totalFrequency: totalFrequency,
                averageFrequency: averageFrequency,
                validFrequencies: validFrequencies.length,
                wordCount: wordList.length
            };
        } catch (error) {
            console.error("Error querying word frequencies:", error);
            throw error;
        }
    }
};

export default DatamuseService;
